import React from 'react';
import { Wrench, LayoutGrid } from 'lucide-react';
import type { AppManifest } from '@yada/types';
import styles from './ToolSwitcher.module.css';
import { getAppIcon } from './appIconRegistry';
import { useSectionArrowNav } from '@yada/ui';

interface ToolSwitcherProps {
    apps: AppManifest[];
    activeId: string | undefined;
    onActivate: (id: string) => void;
    collapsed?: boolean;
    /** App IDs that have direct toolbar access; others are shown in a secondary section */
    pinnedIds?: string[];
}

export const ToolSwitcher: React.FC<ToolSwitcherProps> = ({ apps, activeId, onActivate, collapsed, pinnedIds = [] }) => {
    const listRef = useSectionArrowNav<HTMLDivElement>({ axis: 'vertical' });
    const pinnedSet = new Set(pinnedIds);

    // Home (@yada/app-home) is always direct-access; other apps split by pinned state
    const directApps = apps.filter(a => a.id === '@yada/app-home' || pinnedSet.has(a.id));
    const secondaryApps = apps.filter(a => a.id !== '@yada/app-home' && !pinnedSet.has(a.id));

    const activeIsSecondary = secondaryApps.some(a => a.id === activeId);
    const [otherOpen, setOtherOpen] = React.useState(activeIsSecondary);

    // Expand automatically when a secondary app becomes active
    React.useEffect(() => {
        if (activeIsSecondary) setOtherOpen(true);
    }, [activeIsSecondary]);

    const [canScrollUp, setCanScrollUp] = React.useState(false);
    const [canScrollDown, setCanScrollDown] = React.useState(false);

    const checkScroll = React.useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const hasOverflow = el.scrollHeight > el.clientHeight;
        setCanScrollUp(hasOverflow && el.scrollTop > 2);
        setCanScrollDown(hasOverflow && (el.scrollTop + el.clientHeight < el.scrollHeight - 2));
    }, [listRef]);

    React.useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        checkScroll();
        el.addEventListener('scroll', checkScroll, { passive: true });
        const resizeObserver = new ResizeObserver(() => checkScroll());
        resizeObserver.observe(el);
        return () => {
            el.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
        };
    }, [checkScroll, otherOpen, apps.length, collapsed, listRef]);

    const renderItem = (app: AppManifest) => {
        const Icon = getAppIcon(app.id);
        const isActive = activeId === app.id;
        return (
            <button
                key={app.id}
                type="button"
                className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onActivate(app.id)}
            >
                <span className={styles.itemIcon} aria-hidden>
                    <Icon size={20}/>
                </span>
                <span className={styles.itemName}>{app.name}</span>
            </button>
        );
    };

    return (
        <nav aria-label="Tools" className={styles.root}>
            <div className={styles.header}>
                <Wrench size={20} className={styles.headerIcon}/>
                {!collapsed && <span>Tools</span>}
            </div>
            <div className={styles.listContainer}>
                <div
                    className={`${styles.scrollIndicator} ${styles.scrollIndicatorTop} ${canScrollUp ? styles.scrollIndicatorVisible : ''}`}
                    aria-hidden
                />
                <div ref={listRef} className={styles.list}>
                    {directApps.map(app => renderItem(app))}

                    {secondaryApps.length > 0 && (
                        collapsed
                            ? secondaryApps.map(app => renderItem(app))
                            : (
                                <>
                                    <div className={styles.divider} aria-hidden />
                                    <button
                                        type="button"
                                        className={`${styles.item} ${styles.otherToggle} ${activeIsSecondary && !otherOpen ? styles.itemActive : ''}`}
                                        aria-expanded={otherOpen}
                                        onClick={() => setOtherOpen(o => !o)}
                                    >
                                        <span className={styles.itemIcon} aria-hidden>
                                            <LayoutGrid size={20}/>
                                        </span>
                                        <span className={styles.itemName}>Other apps</span>
                                    </button>
                                    {otherOpen && (
                                        <div className={styles.otherList}>
                                            {secondaryApps.map(app => renderItem(app))}
                                        </div>
                                    )}
                                </>
                            )
                    )}
                </div>
                <div
                    className={`${styles.scrollIndicator} ${styles.scrollIndicatorBottom} ${canScrollDown ? styles.scrollIndicatorVisible : ''}`}
                    aria-hidden
                />
            </div>
        </nav>
    );
};
